import moment from "moment";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { DATE_FORMATS } from "../../../../constants";
import ToggleLinksInput from "../../../../parts/inputs/toggle-links-input";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip-portal";
import CalendarViewToggle from "./CalendarViewToggle";
import MobileAthleteHeader from "./MobileAthleteHeader";

const { DATE_YEAR_FORMAT, DATE_DAY_FORMAT } = DATE_FORMATS;

export function CalendarMonthNav({ activeMonth, onPrev, onNext }) {
  return (
    <div className="flex items-center gap-2  border-solid border border-(--white-10) rounded-lg px-2 py-1">
      <button
        type="button"
        data-button-type="text-white"
        data-icon-only
        onClick={onPrev}
      >
        <SvgIcon src="/img/new-icons/arrow-left.svg" />
      </button>
      <span className="text-nowrap">{activeMonth.format("MMMM, YYYY")}</span>
      <button
        type="button"
        data-button-type="text-white"
        data-icon-only
        onClick={onNext}
      >
        <SvgIcon src="/img/new-icons/arrow-right.svg" />
      </button>
    </div>
  );
}

/**
 * Shared header for calendar views (LogView, PlanView).
 *
 * Props:
 *   activeMonth — moment object; drives month display and navigation
 *   query       — request query object (needs query.month, query.view, query.contentType)
 *   athlete, pathname, locals, viewOptions — forwarded to MobileAthleteHeader
 */
function CalendarMonthHeader({
  activeMonth,
  athlete,
  pathname,
  query,
  locals,
  viewOptions,
}) {
  const { t } = useTranslation("athlete-portal");

  const viewTypeOptions = useMemo(
    () => [
      {
        text: t("calendar.training", "Training"),
        value: "training",
        href: `/actions/refresh?view=month&contentType=training&month=${
          query.month || ""
        }&week=&date=`,
        IconImg: (
          <SvgIcon src="/img/new-icons/barbell.svg" useCurrentColor={true} />
        ),
      },
      {
        text: t("calendar.plan"),
        value: "plan",
        href: `/actions/refresh?view=month&contentType=plan&month=${
          query.month || moment().format(DATE_YEAR_FORMAT)
        }&week=&date=`,
        IconImg: (
          <SvgIcon
            src="/img/new-icons/clipboard-notes.svg"
            useCurrentColor={true}
          />
        ),
      },
      {
        text: t("calendar.log"),
        value: "log",
        href: `/actions/refresh?view=month&contentType=log&month=${
          query.month || moment().format(DATE_YEAR_FORMAT)
        }&week=&date=`,
        IconImg: (
          <SvgIcon
            src="/img/new-icons/document-text.svg"
            useCurrentColor={true}
          />
        ),
      },
    ],
    [query.month, t]
  );

  const periodOptions = useMemo(
    () => [
      {
        text: t("calendar.month"),
        value: "month",
        href: `/actions/refresh?view=month&contentType=training&month=${
          query.month || ""
        }&week=&date=`,
      },
      {
        text: t("calendar.week"),
        value: "week",
        href: `/actions/refresh?view=week&contentType=training&week=${
          moment().isSame(query.month, "month")
            ? moment().format(DATE_DAY_FORMAT)
            : moment(query.month, DATE_YEAR_FORMAT).format(DATE_DAY_FORMAT)
        }&month=&date=`,
      },
      {
        text: t("calendar.day"),
        value: "day",
        href: `/actions/refresh?view=day&contentType=training&date=${
          moment().isSame(query.month, "month")
            ? moment().format(DATE_DAY_FORMAT)
            : moment(query.month, DATE_YEAR_FORMAT).format(DATE_DAY_FORMAT)
        }&month=&week=`,
      },
    ],
    [query.month, t]
  );

  const switchMonth = useCallback(
    (event, direction) => {
      event.preventDefault();
      submit("/actions/refresh?", {
        method: "GET",
        body: {
          month: activeMonth
            .clone()
            .add(direction, "months")
            .format(DATE_YEAR_FORMAT),
          view: query.view,
          contentType: query.contentType,
        },
      });
    },
    [activeMonth, query.view, query.contentType]
  );

  const onPrev = useCallback((e) => switchMonth(e, -1), [switchMonth]);
  const onNext = useCallback((e) => switchMonth(e, 1), [switchMonth]);

  return (
    <>
      <header>
        <section className="gap-6">
          <hgroup className="border-r border-(--white-10) pr-6">
            <span className="text-xs font-semibold text-(--white-40) uppercase tracking-wide">
              {t("calendar.view", "VIEW")}
            </span>
            <CalendarViewToggle name="contentType" options={viewTypeOptions} />
          </hgroup>

          <hgroup>
            <CalendarMonthNav
              activeMonth={activeMonth}
              onPrev={onPrev}
              onNext={onNext}
            />
          </hgroup>

          <hgroup>
            <a
              href={`/actions/refresh?view=month&contentType=training&month=${moment().format(
                DATE_YEAR_FORMAT
              )}&week=&date=`}
              className="this-month-link"
            >
              {t("calendarMonth.thisMonth")}
            </a>
            <ToggleLinksInput name="view" options={periodOptions} />
          </hgroup>
        </section>
      </header>

      <MobileAthleteHeader
        athlete={athlete}
        pathname={pathname}
        query={query}
        locals={locals}
        viewOptions={viewOptions}
      />
    </>
  );
}

export default CalendarMonthHeader;
